import { useMemo, useState } from "react";
import { CalendarClock, CheckSquare, ChevronDown, ChevronUp, CircleAlert, LoaderCircle, Megaphone, PlayCircle, Share2, ShieldCheck, Sparkles } from "lucide-react";


const inputClass = "h-12 rounded-[18px] border border-[#001935]/12 bg-white px-4 text-sm text-[#001935] outline-none transition focus:border-[#D4AF37]";
const slideshowStyle = `
@keyframes marketing-studio-reel-fade {
  0%, 24% { opacity: 1; }
  33%, 100% { opacity: 0; }
}
`;

const platformLabelMap = {
  linkedin: "LinkedIn",
  facebook: "Facebook",
  instagram: "Instagram",
  x: "X",
};

const timelineStatusMap = {
  Draft: ["connection", "render_started", "render_completed", "render_failed", "publish_started"],
  Processing: ["publish_started"],
  Scheduled: ["publish_scheduled", "publish_failed"],
  Published: ["publish_published"],
};

const campaignStatusChipClassMap = {
  Draft: "border-[#D4AF37]/25 bg-[#fff6df] text-[#8b6d16]",
  Publishing: "border-amber-200 bg-amber-50 text-amber-700",
  Scheduled: "border-sky-200 bg-sky-50 text-sky-700",
  Published: "border-emerald-200 bg-emerald-50 text-emerald-700",
  Approved: "border-amber-200 bg-amber-50 text-amber-700",
};

const publishingStatusChipClassMap = {
  publishing_in_progress: "border-amber-200 bg-amber-50 text-amber-700",
  partial: "border-orange-200 bg-orange-50 text-orange-700",
  published: "border-emerald-200 bg-emerald-50 text-emerald-700",
  scheduled: "border-sky-200 bg-sky-50 text-sky-700",
  failed: "border-rose-200 bg-rose-50 text-rose-700",
  blocked: "border-slate-200 bg-slate-50 text-slate-700",
  idle: "border-[#001935]/10 bg-white text-[#42566f]",
};

const getCampaignDisplayStatus = (campaign) => {
  const publishingStatus = campaign?.publishing_result?.status;
  if (publishingStatus === "publishing_in_progress") return "Publishing";
  return campaign?.campaign_status || "Draft";
};

const scopeChipClassMap = {
  Platform_Official: "border-[#001935]/10 bg-[#eef3fa] text-[#001935]",
  Agent_Personal: "border-[#D4AF37]/25 bg-[#fffaf1] text-[#8b6d16]",
};

const toDateTimeInputValue = (value) => {
  if (!value) return "";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value).slice(0, 16);
  const timezoneOffset = parsed.getTimezoneOffset() * 60000;
  return new Date(parsed.getTime() - timezoneOffset).toISOString().slice(0, 16);
};

export const MarketingStudioWorkspace = ({
  campaigns,
  listings,
  loading,
  onApproveAndSchedule,
  onConnectSocialAccount,
  onCreateCampaign,
  onEditCampaignField,
  onSelectCampaign,
  onSocialAccountDraftChange,
  posting,
  roleLabel,
  selectedCampaign,
  selectedListingId,
  setSelectedListingId,
  socialAccountDrafts,
  socialAccounts,
  socialAccountsLoading,
  socialConnectingPlatform,
  timelineEvents,
  timelineLoading,
  updating,
}) => {
  const [ledgerCollapsed, setLedgerCollapsed] = useState(false);
  const [timelineCollapsed, setTimelineCollapsed] = useState(false);
  const [campaignFilter, setCampaignFilter] = useState("All");
  const [timelineFilter, setTimelineFilter] = useState("All");
  const [ledgerDrawerOpen, setLedgerDrawerOpen] = useState(false);
  const [timelineDrawerOpen, setTimelineDrawerOpen] = useState(false);
  const connectedAccounts = (socialAccounts || []).filter((account) => account.token_data?.is_connected);
  const connectedPlatformLabels = connectedAccounts.map((account) => platformLabelMap[account.platform] || account.platform);
  const selectedCampaignScheduleInputValue = toDateTimeInputValue(selectedCampaign?.scheduled_for);
  const selectedCampaignHasSchedule = Boolean(selectedCampaignScheduleInputValue);
  const selectedPublishingStatus = selectedCampaign?.publishing_result?.status || "idle";
  const publishInProgress = selectedPublishingStatus === "publishing_in_progress";
  const reelAssetUrl = selectedCampaign?.content_payloads?.video_reels?.asset_urls?.[0] || selectedCampaign?.content_payloads?.video_reels?.video_url || "";
  const reelHasRenderedVideo = reelAssetUrl.toLowerCase().includes(".mp4");
  const campaignFilters = ["All", "Draft", "Processing", "Scheduled", "Published"];
  const filteredCampaigns = useMemo(() => {
    if (campaignFilter === "All") return campaigns || [];
    if (campaignFilter === "Processing") {
      return (campaigns || []).filter((campaign) => campaign.publishing_result?.status === "publishing_in_progress");
    }
    return (campaigns || []).filter((campaign) => campaign.campaign_status === campaignFilter);
  }, [campaignFilter, campaigns]);
  const filteredTimelineEvents = useMemo(() => {
    if (timelineFilter === "All") return timelineEvents || [];
    return (timelineEvents || []).filter((event) => (timelineStatusMap[timelineFilter] || []).includes(event.event_type));
  }, [timelineEvents, timelineFilter]);
  const campaignFilterCounts = useMemo(() => ({
    All: (campaigns || []).length,
    Draft: (campaigns || []).filter((campaign) => campaign.campaign_status === "Draft").length,
    Processing: (campaigns || []).filter((campaign) => campaign.publishing_result?.status === "publishing_in_progress").length,
    Scheduled: (campaigns || []).filter((campaign) => campaign.campaign_status === "Scheduled").length,
    Published: (campaigns || []).filter((campaign) => campaign.campaign_status === "Published").length,
  }), [campaigns]);
  const timelineFilterCounts = useMemo(() => ({
    All: (timelineEvents || []).length,
    Draft: (timelineEvents || []).filter((event) => (timelineStatusMap.Draft || []).includes(event.event_type)).length,
    Processing: (timelineEvents || []).filter((event) => (timelineStatusMap.Processing || []).includes(event.event_type)).length,
    Scheduled: (timelineEvents || []).filter((event) => (timelineStatusMap.Scheduled || []).includes(event.event_type)).length,
    Published: (timelineEvents || []).filter((event) => (timelineStatusMap.Published || []).includes(event.event_type)).length,
  }), [timelineEvents]);

  return (
    <div className="grid gap-6 overflow-x-hidden pb-28" data-testid="marketing-studio-workspace">
      <style>{slideshowStyle}</style>

      <div className="rounded-[32px] border border-[#001935]/10 bg-[linear-gradient(135deg,#001935_0%,#042143_48%,#0b2f59_100%)] p-6 text-white shadow-[0_24px_70px_rgba(0,25,53,0.18)] md:p-8" data-testid="marketing-studio-hero">
        <p className="text-[11px] font-bold uppercase tracking-[0.24em] text-[#F5D98C]">Marketing Studio</p>
        <div className="mt-4 flex flex-wrap items-end justify-between gap-5">
          <div className="max-w-3xl">
            <h2 className="font-[Cormorant_Garamond,serif] text-4xl font-bold leading-none text-white md:text-5xl" data-testid="marketing-studio-hero-heading">
              Connect social accounts, then publish or schedule campaigns from one studio.
            </h2>
            <p className="mt-4 text-sm leading-8 text-white/78" data-testid="marketing-studio-hero-copy">
              {roleLabel === "Platform_Official"
                ? "You are working in the platform-wide studio. Connect official SG Prop Portal social accounts here, then push approved campaigns through the publishing queue."
                : "You are working inside the agent studio. Connect your own personal social accounts here, then publish or schedule listing campaigns from the same workspace."}
            </p>
          </div>
          <div className="rounded-[24px] border border-white/12 bg-white/8 px-5 py-5" data-testid="marketing-studio-target-card">
            <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#F5D98C]">Publishing Target</p>
            <p className="mt-3 text-2xl font-semibold text-white">{roleLabel === "Platform_Official" ? "Platform Official" : "Agent Personal"}</p>
          </div>
        </div>
      </div>

      <div className="grid gap-6 xl:grid-cols-[1.15fr_0.85fr]" data-testid="marketing-social-accounts-grid">
        <div className="rounded-[30px] border border-[#001935]/10 bg-white/92 p-6 shadow-[0_20px_60px_rgba(0,25,53,0.06)]" data-testid="marketing-social-accounts-panel">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="max-w-3xl">
              <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#8b6d16]">Social Accounts</p>
              <h3 className="mt-2 text-3xl font-semibold text-[#001935]" data-testid="marketing-social-accounts-heading">OAuth settings inside the workspace</h3>
              <p className="mt-3 text-sm leading-7 text-[#42566f]" data-testid="marketing-social-accounts-copy">
                Connect a platform once and the workspace will keep the account state visible after refresh, so your publishing queue stays ready.
              </p>
            </div>
            <div className="rounded-full border border-[#D4AF37]/25 bg-[#fff8e8] px-4 py-2 text-[11px] font-bold uppercase tracking-[0.18em] text-[#8b6d16]" data-testid="marketing-social-accounts-mocked-pill">
              CONNECTION READY
            </div>
          </div>

          <div className="mt-6 grid gap-4 md:grid-cols-2" data-testid="marketing-social-accounts-list">
            {(socialAccounts || []).map((account) => {
              const isConnecting = socialConnectingPlatform === account.platform;
              return (
                <div className="rounded-[24px] border border-[#001935]/10 bg-[#fffaf1] p-5" data-testid={`marketing-social-account-card-${account.platform}`} key={account.platform}>
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#8b6d16]">{platformLabelMap[account.platform] || account.platform}</p>
                      <p className="mt-2 text-lg font-semibold text-[#001935]" data-testid={`marketing-social-account-scope-${account.platform}`}>
                        {roleLabel === "Platform_Official" ? "Platform Official" : "Agent Personal"}
                      </p>
                    </div>
                    <div className={`rounded-full border px-3 py-1 text-[11px] font-bold uppercase tracking-[0.16em] ${account.token_data?.is_connected ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-[#001935]/10 bg-white text-[#42566f]"}`} data-testid={`marketing-social-account-status-${account.platform}`}>
                      {account.token_data?.is_connected ? "Connected" : "Not connected"}
                    </div>
                  </div>

                  <label className="mt-4 block text-xs font-bold uppercase tracking-[0.16em] text-[#42566f]" data-testid={`marketing-social-account-page-id-label-${account.platform}`} htmlFor={`marketing-social-account-page-id-${account.platform}`}>
                    Profile / Page ID
                  </label>
                  <input
                    className={`${inputClass} mt-2 w-full`}
                    data-testid={`marketing-social-account-page-id-input-${account.platform}`}
                    id={`marketing-social-account-page-id-${account.platform}`}
                    onChange={(event) => onSocialAccountDraftChange(account.platform, event.target.value)}
                    placeholder={`${account.platform}-profile-id`}
                    value={socialAccountDrafts?.[account.platform] || ""}
                  />

                  <div className="mt-4 rounded-[18px] border border-[#001935]/8 bg-white px-4 py-3" data-testid={`marketing-social-account-token-card-${account.platform}`}>
                    <p className="text-xs font-semibold uppercase tracking-[0.14em] text-[#8b6d16]">Token state</p>
                    <p className="mt-2 text-sm text-[#42566f]" data-testid={`marketing-social-account-token-state-${account.platform}`}>
                      {account.token_data?.is_connected ? "Account Connected Successfully" : "Ready to connect this account"}
                    </p>
                  </div>

                  <button
                    className="gold-button mt-4 !justify-center"
                    data-testid={`marketing-social-account-connect-button-${account.platform}`}
                    disabled={isConnecting || socialAccountsLoading}
                    onClick={() => onConnectSocialAccount(account.platform)}
                    type="button"
                  >
                    {isConnecting ? <LoaderCircle className="mr-2 h-4 w-4 animate-spin" /> : <ShieldCheck className="mr-2 h-4 w-4" />}
                    {account.token_data?.is_connected ? "Reconnect account" : "Connect account"}
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        <div className="rounded-[30px] border border-[#001935]/10 bg-white/92 p-6 shadow-[0_20px_60px_rgba(0,25,53,0.06)]" data-testid="marketing-publishing-pipeline-panel">
          <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#8b6d16]">Publishing Pipeline</p>
          <h3 className="mt-2 text-3xl font-semibold text-[#001935]">Broadcaster logic</h3>
          <div className="mt-5 grid gap-3">
            <div className="rounded-[22px] border border-[#001935]/10 bg-[#fffaf1] px-4 py-4" data-testid="marketing-publishing-mode-card">
              <div className="flex items-center gap-2 text-[#8b6d16]"><CircleAlert className="h-4 w-4" /><p className="text-[11px] font-bold uppercase tracking-[0.16em]">Social Queue Active</p></div>
              <p className="mt-3 text-sm leading-7 text-[#42566f]">Leave the schedule blank to publish immediately. Add a date and the background listener will move it through the social queue when the time arrives.</p>
            </div>

            <div className="rounded-[22px] border border-[#001935]/10 bg-[#f8f8f6] px-4 py-4" data-testid="marketing-connected-platforms-summary">
              <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#8b6d16]">Connected platforms</p>
              <p className="mt-2 text-sm leading-7 text-[#42566f]" data-testid="marketing-connected-platforms-value">
                {connectedPlatformLabels.length ? connectedPlatformLabels.join(", ") : "No social accounts connected yet."}
              </p>
            </div>

            {selectedCampaign ? (
              <div className="rounded-[22px] border border-[#001935]/10 bg-white px-4 py-4" data-testid="marketing-selected-campaign-publish-summary">
                <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#8b6d16]">Selected campaign status</p>
                <p className="mt-2 text-lg font-semibold text-[#001935]" data-testid="marketing-selected-campaign-status-value">{getCampaignDisplayStatus(selectedCampaign)}</p>
                <p className="mt-2 text-sm leading-7 text-[#42566f]" data-testid="marketing-selected-campaign-status-note">{selectedCampaign.publishing_result?.message || "Ready for the next publishing action."}</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <span className="rounded-full border border-[#001935]/10 bg-[#f8f8f6] px-3 py-1 text-[11px] font-bold uppercase tracking-[0.16em] text-[#42566f]" data-testid="marketing-live-publisher-badge">Publisher • {selectedCampaign.publishing_result?.provider === "AYRSHARE" ? "Ayrshare Live" : "Queue Ready"}</span>
                  <span className="rounded-full border border-[#001935]/10 bg-[#f8f8f6] px-3 py-1 text-[11px] font-bold uppercase tracking-[0.16em] text-[#42566f]" data-testid="marketing-live-renderer-badge">Renderer • {selectedCampaign.video_render_result?.provider === "CREATOMATE_READY" && selectedCampaign.video_render_result?.request_payload && Object.keys(selectedCampaign.video_render_result?.request_payload || {}).length ? "Creatomate Ready" : "Studio Engine"}</span>
                  <span className={`rounded-full border px-3 py-1 text-[11px] font-bold uppercase tracking-[0.16em] ${publishingStatusChipClassMap[selectedPublishingStatus] || publishingStatusChipClassMap.idle}`} data-testid="marketing-selected-campaign-publishing-status-chip">
                    Pipeline • {selectedPublishingStatus.replaceAll("_", " ")}
                  </span>
                </div>
                {publishInProgress ? (
                  <div className="mt-4 rounded-[20px] border border-amber-200 bg-[linear-gradient(135deg,#fff8e6_0%,#fff2c7_100%)] px-4 py-4 text-[#7c5d09]" data-testid="marketing-publishing-in-progress-banner">
                    <div className="flex items-start gap-3">
                      <div className="mt-0.5 rounded-full bg-white/70 p-2 text-amber-700">
                        <Sparkles className="h-4 w-4" />
                      </div>
                      <div>
                        <p className="text-xs font-bold uppercase tracking-[0.16em]">Publishing in progress</p>
                        <p className="mt-2 text-sm leading-7">
                          Ayrshare is processing this post in the background. You can keep working here — the final result will appear automatically once the webhook returns.
                        </p>
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>
      </div>

      <div className="grid gap-6" data-testid="marketing-studio-main-grid">
        <div className="rounded-[30px] border border-[#001935]/10 bg-white/92 p-6 shadow-[0_20px_60px_rgba(0,25,53,0.06)]">
          <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#8b6d16]">Campaign Initializer</p>
          <h3 className="mt-2 text-3xl font-semibold text-[#001935]">Choose a listing to stage</h3>
          <p className="mt-3 text-sm leading-7 text-[#42566f]">Campaigns still start as editable drafts, but the studio now lets you push them live immediately or queue them for scheduled publishing.</p>

          <div className="mt-6 grid gap-3">
            <select className={inputClass} data-testid="marketing-studio-listing-select" onChange={(event) => setSelectedListingId(event.target.value)} value={selectedListingId}>
              <option value="">Select a listing</option>
              {listings.map((listing) => (
                <option key={listing.id} value={listing.id}>{`${listing.title} • ${listing.district} • ${listing.owner_name || listing.owner_id || "Internal"}`}</option>
              ))}
            </select>
            <button className="gold-button !justify-center" data-testid="marketing-studio-create-campaign-button" disabled={!selectedListingId || posting} onClick={onCreateCampaign} type="button">
              {posting ? <LoaderCircle className="mr-2 h-4 w-4 animate-spin" /> : <Megaphone className="mr-2 h-4 w-4" />} Initialize Draft
            </button>
          </div>

        </div>

        <div className="rounded-[30px] border border-[#001935]/10 bg-white/92 p-6 shadow-[0_20px_60px_rgba(0,25,53,0.06)]" data-testid="marketing-studio-staging-grid-panel">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#8b6d16]">Staging Grid</p>
              <h3 className="mt-2 text-3xl font-semibold text-[#001935]">Platform-specific preview cards</h3>
            </div>
            <button className="gold-button" data-testid="marketing-studio-approve-schedule-button" disabled={!selectedCampaign || updating || publishInProgress} onClick={onApproveAndSchedule} type="button">
              {updating ? <LoaderCircle className="mr-2 h-4 w-4 animate-spin" /> : <CheckSquare className="mr-2 h-4 w-4" />} {selectedCampaignHasSchedule ? "Approve & Schedule" : "Approve & Publish Now"}
            </button>
          </div>

          {!selectedCampaign ? (
            <div className="mt-6 rounded-[24px] border border-dashed border-[#001935]/12 bg-[#fffaf1] px-5 py-10 text-center text-sm leading-7 text-[#6b6252]" data-testid="marketing-studio-editor-empty">
              Select or initialize a draft to open the preview cards.
            </div>
          ) : (
            <>
              <div className="sticky bottom-4 z-20 mt-6" data-testid="marketing-studio-sticky-action-bar">
                <div className="mb-3 flex justify-start sm:justify-end" data-testid="marketing-studio-sticky-summary-row">
                  <div className="flex max-w-full flex-wrap items-center gap-2 rounded-[20px] border border-[#001935]/10 bg-white/95 px-4 py-2 text-xs font-bold uppercase tracking-[0.16em] text-[#001935] shadow-[0_10px_24px_rgba(0,25,53,0.12)] backdrop-blur sm:rounded-full" data-testid="marketing-studio-sticky-summary-chip">
                    <span className="mr-2 text-[#8b6d16]">Selected Campaign</span>
                    <span className="min-w-0 break-words normal-case tracking-normal text-[#001935]">{selectedCampaign?.listing_title || "Untitled campaign"}</span>
                    <span className={`ml-3 rounded-full border px-3 py-1 text-[10px] font-bold uppercase tracking-[0.14em] ${campaignStatusChipClassMap[selectedCampaign?.campaign_status] || "border-[#001935]/10 bg-white text-[#42566f]"}`} data-testid="marketing-studio-sticky-summary-status-chip">
                      {selectedCampaign?.campaign_status || "Draft"}
                    </span>
                  </div>
                </div>
                <div className="rounded-[24px] border border-[#001935]/10 bg-white/95 px-4 py-4 shadow-[0_16px_40px_rgba(0,25,53,0.12)] backdrop-blur">
                  <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
                    <div className="min-w-0 flex-1">
                      <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#8b6d16]">Sticky Action Bar</p>
                      <p className="mt-2 text-sm leading-7 text-[#42566f]">Keep schedule and approval controls in reach while scrolling long campaigns.</p>
                    </div>
                    <div className="grid gap-3 sm:grid-cols-[minmax(0,20rem)_auto] sm:items-end xl:min-w-[28rem]">
                      <div>
                        <label className="text-xs font-bold uppercase tracking-[0.16em] text-[#42566f]" data-testid="marketing-studio-sticky-schedule-label" htmlFor="marketing-studio-sticky-schedule-input">
                          Schedule Time
                        </label>
                        <input className={`${inputClass} mt-2 w-full`} data-testid="marketing-studio-sticky-schedule-input" id="marketing-studio-sticky-schedule-input" onChange={(event) => onEditCampaignField("root", "scheduled_for", event.target.value)} onInput={(event) => onEditCampaignField("root", "scheduled_for", event.currentTarget.value)} type="datetime-local" value={selectedCampaignScheduleInputValue} />
                      </div>
                      <button className="gold-button h-12 sm:min-w-[15rem]" data-testid="marketing-studio-sticky-approve-button" disabled={!selectedCampaign || updating || publishInProgress} onClick={onApproveAndSchedule} type="button">
                        {updating ? <LoaderCircle className="mr-2 h-4 w-4 animate-spin" /> : <CheckSquare className="mr-2 h-4 w-4" />} {selectedCampaignHasSchedule ? "Approve & Schedule" : "Approve & Publish Now"}
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-6 grid gap-5 lg:grid-cols-2 2xl:grid-cols-3" data-testid="marketing-studio-preview-grid">
                {[
                  ["facebook_instagram", "Facebook / Instagram", Share2, "text", "image_url"],
                  ["linkedin", "LinkedIn", Megaphone, "text", "image_url"],
                  ["video_reels", "Reels / TikTok", PlayCircle, "text", "video_url"],
                ].map(([key, label, Icon, bodyField, mediaField]) => (
                  <div className="rounded-[24px] border border-[#001935]/10 bg-[#fffaf1] p-5 xl:p-6" data-testid={`marketing-studio-card-${key}`} key={key}>
                    <div className="flex items-center gap-2 text-[#8b6d16]"><Icon className="h-4 w-4" /><p className="text-[11px] font-bold uppercase tracking-[0.16em]">{label}</p></div>
                    {key === "video_reels" ? (
                      <div className="relative mt-4 aspect-[9/16] overflow-hidden rounded-[20px] border border-[#001935]/10 bg-[#001935]" data-testid="marketing-studio-reels-slideshow">
                        {reelHasRenderedVideo ? (
                          <video className="h-full w-full object-cover" controls data-testid="marketing-studio-reels-video-player" poster={(selectedCampaign.content_payloads?.video_reels?.image_urls || [])[0] || undefined}>
                            <source src={reelAssetUrl} type="video/mp4" />
                          </video>
                        ) : (selectedCampaign.content_payloads?.video_reels?.image_urls || []).length ? (
                          (selectedCampaign.content_payloads.video_reels.image_urls || []).slice(0, 5).map((imageUrl, index) => (
                            <img
                              alt={`Reel slide ${index + 1}`}
                              className="absolute inset-0 h-full w-full object-cover"
                              data-testid={`marketing-studio-reels-slide-${index + 1}`}
                              key={`${imageUrl}-${index}`}
                              src={imageUrl}
                              style={{ animation: `marketing-studio-reel-fade 12s infinite`, animationDelay: `${index * 2.4}s`, opacity: index === 0 ? 1 : 0 }}
                            />
                          ))
                        ) : (
                          <div className="flex h-full items-center justify-center px-6 text-center text-sm text-white/80">Add listing images to preview the future reel slideshow.</div>
                        )}
                      </div>
                    ) : selectedCampaign.content_payloads?.[key]?.image_url ? (
                      <div className="mt-4 overflow-hidden rounded-[20px] border border-[#001935]/10 bg-[#001935]/4" data-testid={`marketing-studio-card-preview-${key}`}>
                        <img alt={`${label} preview`} className="aspect-[4/5] w-full object-cover" src={selectedCampaign.content_payloads?.[key]?.image_url} />
                      </div>
                    ) : null}
                    <textarea className="mt-4 min-h-[170px] w-full rounded-[20px] border border-[#001935]/12 bg-white px-4 py-4 text-sm leading-7 text-[#001935] outline-none transition focus:border-[#D4AF37]" data-testid={`marketing-studio-card-body-${key}`} onChange={(event) => onEditCampaignField(key, bodyField, event.target.value)} value={selectedCampaign.content_payloads?.[key]?.[bodyField] || ""} />
                    <input className={`${inputClass} mt-4 w-full`} data-testid={`marketing-studio-card-media-${key}`} onChange={(event) => onEditCampaignField(key, mediaField, event.target.value)} placeholder={mediaField === "video_url" ? "Video URL placeholder" : "Image URL placeholder"} value={selectedCampaign.content_payloads?.[key]?.[mediaField] || ""} />
                    {key === "video_reels" && selectedCampaign.video_render_result?.asset_urls?.length ? (
                      <div className="mt-4 rounded-[18px] border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800" data-testid="marketing-studio-video-engine-status">
                        Video Engine Ready • {selectedCampaign.video_render_result.message}
                      </div>
                    ) : null}
                    <div className="mt-4 rounded-full border border-[#001935]/10 bg-white px-4 py-2 text-xs font-bold uppercase tracking-[0.14em] text-[#42566f]" data-testid={`marketing-studio-card-status-${key}`}>{selectedCampaign.content_payloads?.[key]?.status || "Draft"}</div>
                  </div>
                ))}
              </div>

              <div className="mt-6 rounded-[24px] border border-[#001935]/10 bg-[#f8f8f6] px-5 py-5" data-testid="marketing-studio-schedule-panel">
                <div className="flex items-center gap-2 text-[#8b6d16]"><CalendarClock className="h-4 w-4" /><p className="text-[11px] font-bold uppercase tracking-[0.16em]">Scheduling</p></div>
                <input className={`${inputClass} mt-4 w-full sm:max-w-[20rem]`} data-testid="marketing-studio-schedule-input" onChange={(event) => onEditCampaignField("root", "scheduled_for", event.target.value)} onInput={(event) => onEditCampaignField("root", "scheduled_for", event.currentTarget.value)} type="datetime-local" value={selectedCampaignScheduleInputValue} />
                <p className="mt-3 text-sm leading-7 text-[#42566f]" data-testid="marketing-studio-schedule-note">Leave this empty to publish immediately. Add a date to move the campaign into the scheduled social queue.</p>
                <div className="mt-4 rounded-[18px] border border-[#001935]/10 bg-white px-4 py-4" data-testid="marketing-studio-publishing-result-card">
                  <p className="text-xs font-bold uppercase tracking-[0.16em] text-[#8b6d16]">Latest pipeline result</p>
                  <p className="mt-2 text-sm leading-7 text-[#42566f]" data-testid="marketing-studio-publishing-result-message">{selectedCampaign.publishing_result?.message || "Publishing pipeline ready."}</p>
                  {publishInProgress ? <div className="mt-3 inline-flex items-center rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.16em] text-amber-700" data-testid="marketing-studio-publishing-result-processing-pill"><LoaderCircle className="mr-2 h-3.5 w-3.5 animate-spin" /> Waiting for webhook confirmation</div> : null}
                </div>
              </div>

            </>
          )}
        </div>

        <div className="rounded-[30px] border border-[#001935]/10 bg-white/92 p-6 shadow-[0_20px_60px_rgba(0,25,53,0.06)]" data-testid="marketing-studio-ledger-panel">
          <div className="rounded-[24px] border border-[#D4AF37]/18 bg-[#fffaf1] px-4 py-4" data-testid="marketing-studio-campaign-ledger">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#8b6d16]">Campaign Ledger</p>
                <p className="mt-2 text-sm leading-7 text-[#42566f]">Centralized view of draft, scheduled, and published social campaigns.</p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <div className="rounded-full border border-[#001935]/10 bg-white px-4 py-2 text-xs font-bold uppercase tracking-[0.16em] text-[#42566f]" data-testid="marketing-studio-campaign-count-pill">{filteredCampaigns.length} of {campaigns.length} campaigns</div>
                <button className="rounded-full border border-[#001935]/10 bg-white px-4 py-2 text-xs font-bold uppercase tracking-[0.16em] text-[#42566f] transition hover:border-[#D4AF37]/40 hover:text-[#001935]" data-testid="marketing-studio-ledger-toggle" onClick={() => setLedgerCollapsed((current) => !current)} type="button">
                  {ledgerCollapsed ? <ChevronDown className="mr-2 inline h-4 w-4" /> : <ChevronUp className="mr-2 inline h-4 w-4" />}
                  {ledgerCollapsed ? "Expand Ledger" : "Collapse Ledger"}
                </button>
              </div>
            </div>
            <div className="mt-4 md:hidden" data-testid="marketing-studio-campaign-filter-drawer-mobile">
              <button className="flex w-full items-center justify-between rounded-[18px] border border-[#001935]/10 bg-white px-4 py-3 text-left text-sm font-semibold text-[#001935]" data-testid="marketing-studio-campaign-filter-drawer-toggle" onClick={() => setLedgerDrawerOpen((current) => !current)} type="button">
                <span>Ledger Filters • {campaignFilter} ({campaignFilterCounts[campaignFilter] ?? 0})</span>
                {ledgerDrawerOpen ? <ChevronUp className="h-4 w-4 text-[#8b6d16]" /> : <ChevronDown className="h-4 w-4 text-[#8b6d16]" />}
              </button>
              {ledgerDrawerOpen ? <div className="mt-3 grid gap-2 rounded-[20px] border border-[#001935]/10 bg-white p-3" data-testid="marketing-studio-campaign-filter-drawer-panel">
                {campaignFilters.map((filterLabel) => (
                  <button
                    className={`rounded-[16px] border px-4 py-3 text-left text-xs font-bold uppercase tracking-[0.16em] transition ${campaignFilter === filterLabel ? "border-[#D4AF37]/40 bg-[#D4AF37]/18 text-[#001935]" : "border-[#001935]/10 bg-white text-[#42566f]"}`}
                    data-testid={`marketing-studio-campaign-filter-${filterLabel.toLowerCase()}`}
                    key={filterLabel}
                    onClick={() => {
                      setCampaignFilter(filterLabel);
                      setLedgerDrawerOpen(false);
                    }}
                    type="button"
                  >
                    {filterLabel} ({campaignFilterCounts[filterLabel] ?? 0})
                  </button>
                ))}
              </div> : null}
            </div>
            <div className="mt-4 hidden flex-wrap gap-2 md:flex" data-testid="marketing-studio-campaign-filters">
              {campaignFilters.map((filterLabel) => (
                <button
                  className={`rounded-full border px-4 py-2 text-xs font-bold uppercase tracking-[0.16em] transition ${campaignFilter === filterLabel ? "border-[#D4AF37]/40 bg-[#D4AF37]/18 text-[#001935]" : "border-[#001935]/10 bg-white text-[#42566f] hover:border-[#D4AF37]/25 hover:text-[#001935]"}`}
                  data-testid={`marketing-studio-campaign-filter-desktop-${filterLabel.toLowerCase()}`}
                  key={filterLabel}
                  onClick={() => setCampaignFilter(filterLabel)}
                  type="button"
                >
                  {filterLabel} ({campaignFilterCounts[filterLabel] ?? 0})
                </button>
              ))}
            </div>
            {!ledgerCollapsed ? <div className="mt-4 grid gap-3 xl:grid-cols-2 2xl:grid-cols-4" data-testid="marketing-studio-campaign-list">
              {filteredCampaigns.length ? filteredCampaigns.map((campaign) => (
                <button className={`min-w-0 rounded-[20px] border px-4 py-3 text-left transition ${selectedCampaign?.id === campaign.id ? "border-[#D4AF37]/40 bg-white" : "border-[#001935]/8 bg-[#fffdf8] hover:border-[#D4AF37]/25"}`} data-testid={`marketing-studio-campaign-row-${campaign.id}`} key={campaign.id} onClick={() => onSelectCampaign(campaign)} type="button">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="break-words text-sm font-semibold text-[#001935]">{campaign.listing_title}</p>
                      <div className="mt-2 flex flex-wrap gap-2">
                        <span className={`rounded-full border px-3 py-1 text-[11px] font-bold uppercase tracking-[0.14em] ${scopeChipClassMap[campaign.publishing_target] || "border-[#001935]/10 bg-white text-[#42566f]"}`} data-testid={`marketing-studio-campaign-scope-chip-${campaign.id}`}>
                          {campaign.publishing_target === "Platform_Official" ? "Platform Official" : "Agent Personal"}
                        </span>
                        <span className={`rounded-full border px-3 py-1 text-[11px] font-bold uppercase tracking-[0.14em] ${campaignStatusChipClassMap[getCampaignDisplayStatus(campaign)] || "border-[#001935]/10 bg-white text-[#42566f]"}`} data-testid={`marketing-studio-campaign-status-chip-${campaign.id}`}>
                          {getCampaignDisplayStatus(campaign)}
                        </span>
                      </div>
                    </div>
                    <span className="text-xs uppercase tracking-[0.14em] text-[#42566f]">{campaign.created_at ? new Date(campaign.created_at).toLocaleDateString("en-SG") : "Draft"}</span>
                  </div>
                </button>
              )) : <div className="rounded-[20px] border border-dashed border-[#001935]/12 bg-white px-4 py-5 text-sm leading-7 text-[#6b6252] xl:col-span-2 2xl:col-span-4" data-testid="marketing-studio-campaign-empty">No campaigns match the current filter yet.</div>}
            </div> : null}
          </div>
        </div>

        <div className="rounded-[30px] border border-[#001935]/10 bg-white/92 p-6 shadow-[0_20px_60px_rgba(0,25,53,0.06)]" data-testid="marketing-studio-timeline-wrapper-panel">
          <div className="rounded-[24px] border border-[#001935]/10 bg-white px-5 py-5" data-testid="marketing-studio-timeline-panel">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#8b6d16]">Render History & Publish Timeline</p>
                <p className="mt-2 text-sm leading-7 text-[#42566f]">A lightweight audit trail of connections, render states, scheduling, publishing, and failures for this campaign.</p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <div className="rounded-full border border-[#001935]/10 bg-[#f8f8f6] px-4 py-2 text-xs font-bold uppercase tracking-[0.16em] text-[#42566f]" data-testid="marketing-studio-timeline-count">
                  {filteredTimelineEvents.length} of {(timelineEvents || []).length} events
                </div>
                <button className="rounded-full border border-[#001935]/10 bg-white px-4 py-2 text-xs font-bold uppercase tracking-[0.16em] text-[#42566f] transition hover:border-[#D4AF37]/40 hover:text-[#001935]" data-testid="marketing-studio-timeline-toggle" onClick={() => setTimelineCollapsed((current) => !current)} type="button">
                  {timelineCollapsed ? <ChevronDown className="mr-2 inline h-4 w-4" /> : <ChevronUp className="mr-2 inline h-4 w-4" />}
                  {timelineCollapsed ? "Expand Timeline" : "Collapse Timeline"}
                </button>
              </div>
            </div>

            <div className="mt-4 md:hidden" data-testid="marketing-studio-timeline-filter-drawer-mobile">
              <button className="flex w-full items-center justify-between rounded-[18px] border border-[#001935]/10 bg-white px-4 py-3 text-left text-sm font-semibold text-[#001935]" data-testid="marketing-studio-timeline-filter-drawer-toggle" onClick={() => setTimelineDrawerOpen((current) => !current)} type="button">
                <span>Timeline Filters • {timelineFilter} ({timelineFilterCounts[timelineFilter] ?? 0})</span>
                {timelineDrawerOpen ? <ChevronUp className="h-4 w-4 text-[#8b6d16]" /> : <ChevronDown className="h-4 w-4 text-[#8b6d16]" />}
              </button>
              {timelineDrawerOpen ? <div className="mt-3 grid gap-2 rounded-[20px] border border-[#001935]/10 bg-white p-3" data-testid="marketing-studio-timeline-filter-drawer-panel">
                {campaignFilters.map((filterLabel) => (
                  <button
                    className={`rounded-[16px] border px-4 py-3 text-left text-xs font-bold uppercase tracking-[0.16em] transition ${timelineFilter === filterLabel ? "border-[#D4AF37]/40 bg-[#D4AF37]/18 text-[#001935]" : "border-[#001935]/10 bg-white text-[#42566f]"}`}
                    data-testid={`marketing-studio-timeline-filter-${filterLabel.toLowerCase()}`}
                    key={filterLabel}
                    onClick={() => {
                      setTimelineFilter(filterLabel);
                      setTimelineDrawerOpen(false);
                    }}
                    type="button"
                  >
                    {filterLabel} ({timelineFilterCounts[filterLabel] ?? 0})
                  </button>
                ))}
              </div> : null}
            </div>
            <div className="mt-4 hidden flex-wrap gap-2 md:flex" data-testid="marketing-studio-timeline-filters">
              {campaignFilters.map((filterLabel) => (
                <button
                  className={`rounded-full border px-4 py-2 text-xs font-bold uppercase tracking-[0.16em] transition ${timelineFilter === filterLabel ? "border-[#D4AF37]/40 bg-[#D4AF37]/18 text-[#001935]" : "border-[#001935]/10 bg-white text-[#42566f] hover:border-[#D4AF37]/25 hover:text-[#001935]"}`}
                  data-testid={`marketing-studio-timeline-filter-desktop-${filterLabel.toLowerCase()}`}
                  key={filterLabel}
                  onClick={() => setTimelineFilter(filterLabel)}
                  type="button"
                >
                  {filterLabel} ({timelineFilterCounts[filterLabel] ?? 0})
                </button>
              ))}
            </div>

            {!timelineCollapsed ? <div className="mt-5 grid gap-3" data-testid="marketing-studio-timeline-list">
              {timelineLoading ? <div className="rounded-[18px] border border-[#001935]/10 bg-[#f8f8f6] px-4 py-4 text-sm text-[#42566f]" data-testid="marketing-studio-timeline-loading"><LoaderCircle className="mr-2 inline h-4 w-4 animate-spin text-[#8b6d16]" /> Loading timeline…</div> : null}
              {!timelineLoading && !(filteredTimelineEvents || []).length ? <div className="rounded-[18px] border border-dashed border-[#001935]/12 bg-[#f8f8f6] px-4 py-4 text-sm text-[#42566f]" data-testid="marketing-studio-timeline-empty">No timeline events match the current filter yet.</div> : null}
              {filteredTimelineEvents.map((event) => (
                <div className="min-w-0 rounded-[18px] border border-[#001935]/10 bg-[#f8f8f6] px-4 py-4" data-testid={`marketing-studio-timeline-event-${event.id}`} key={event.id}>
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="break-words text-sm font-semibold text-[#001935]">{event.title}</p>
                      <p className="mt-1 break-words text-xs uppercase tracking-[0.16em] text-[#8b6d16]">{event.source} • {event.status}</p>
                    </div>
                    <span className="max-w-full break-words text-xs uppercase tracking-[0.16em] text-[#42566f]">{event.created_at ? new Date(event.created_at).toLocaleString("en-SG") : "Now"}</span>
                  </div>
                  <p className="mt-3 break-words text-sm leading-7 text-[#42566f]">{event.description}</p>
                </div>
              ))}
            </div> : null}
          </div>
        </div>
      </div>

      {loading ? <div className="rounded-[24px] border border-[#001935]/10 bg-white px-5 py-6 text-sm text-[#42566f]" data-testid="marketing-studio-loading"><LoaderCircle className="mr-2 inline h-4 w-4 animate-spin text-[#8b6d16]" /> Loading marketing studio…</div> : null}
    </div>
  );
};